/**
 * SwitchToFosButton — Cross-domain nav entry point (PLATFORM-003.2).
 *
 * Renders a header-bar button that mints a one-time cross-domain authorization
 * via identity-service and redirects to founderyos.dev, landing the user already
 * authenticated (via the exchange flow in PLATFORM-003.1 + 003.3).
 *
 * Cross-domain handoff (dac-001-5): `@hello-world-dao/auth@^2.2.2`'s
 * `navigateCrossDomain` generates the PKCE verifier client-side and calls
 * `POST {baseUrl}/cross-domain/authorize` (Bearer identity JWT) on
 * **identity-service** — the NATIVE route on the DAO-side identity host (the
 * `/api/v1/auth/*` prefix is the FOS-edge convention only and is stripped
 * FOS-side, so the installed auth package composes the bare native route here).
 * `baseUrl` is therefore the identity-service base
 * (`VITE_IDENTITY_SERVICE_URL`, via {@link getIdentityServiceUrl}) — the SAME
 * base the suite uses for token exchange/refresh/logout — NOT
 * `VITE_ORACLE_BRIDGE_URL`, which it pointed at before the Phase 2 PKCE rewrite.
 *
 * Hidden when the user isn't logged in. On `session-expired` errors, redirects
 * the member straight to the FOS DASHBOARD — the member is already authenticated
 * and switching products, so the dashboard handles auth itself (no login
 * front-door bounce). The old think-tank login host is DEPRECATED (unified under
 * FounderyOS) and dead — never redirect there.
 *
 * @module components/layout/SwitchToFosButton
 */

import { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import {
  navigateCrossDomain,
  CrossDomainAuthError,
  useAuth,
} from '@hello-world-dao/auth';
import { getIdentityJwt } from '@/services/identity-jwt-store';
import { getIdentityServiceUrl } from '@/services/identity-service-client';
import { resolveFosDashboardUrl } from './fos-frontdoor-url';

function resolveFosDomain(): string {
  const env = import.meta.env.VITE_FOUNDERYOS_DOMAIN;
  if (typeof env === 'string' && env.length > 0) {
    return env;
  }
  return 'founderyos.dev';
}

export interface SwitchToFosButtonProps {
  targetPath?: string;
  className?: string;
}

export function SwitchToFosButton({
  targetPath = '/dashboard',
  className,
}: SwitchToFosButtonProps) {
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  if (!isAuthenticated) {
    return null;
  }

  async function onClick() {
    setLoading(true);
    setErrorMsg(null);
    try {
      await navigateCrossDomain(resolveFosDomain(), targetPath, {
        // dac-001-5: `baseUrl` is the identity-service base. The installed auth
        // package (^2.2.2 PKCE rewrite) composes the NATIVE path
        // `{baseUrl}/cross-domain/authorize` on the DAO-side identity host (the
        // `/api/v1/auth/*` strip is FOS-side only), NOT oracle-bridge. Use the
        // same accessor (`getIdentityServiceUrl` → `VITE_IDENTITY_SERVICE_URL`)
        // the suite already uses for token exchange/refresh/logout so the
        // authorize call and the rest of the identity-service traffic never drift.
        baseUrl: getIdentityServiceUrl(),
        entryProduct: 'fos',
        // Unified-identity members (ADR-015/ADR-024) authenticate via the
        // identity-service in-memory JWT + httpOnly hwdao_refresh cookie and have
        // NO legacy oracle-bridge access_token session cookie. Pass the JWT so the
        // cross-domain mint authorizes via its requireSessionAuth JWKS path;
        // without it the cookie-only mint 401s and the member lands on the FOS
        // dashboard logged-out.
        getAccessToken: () => getIdentityJwt(),
      });
    } catch (err) {
      setLoading(false);
      if (err instanceof CrossDomainAuthError && err.code === 'session-expired') {
        // Already-authenticated member switching products → send straight to the
        // FOS dashboard, which handles auth itself (no login front-door bounce).
        // The old think-tank login host is deprecated + dead.
        window.location.href = resolveFosDashboardUrl();
        return;
      }
      const msg =
        err instanceof Error ? err.message : 'Could not switch to FounderyOS';
      setErrorMsg(msg);
    }
  }

  return (
    <div className={className}>
      <button
        type="button"
        onClick={onClick}
        disabled={loading}
        data-testid="switch-to-fos-button"
        className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
      >
        <ExternalLink className="h-4 w-4" />
        {loading ? 'Switching...' : 'Switch to FOS'}
      </button>
      {errorMsg && (
        <p
          role="alert"
          className="mt-1 text-xs text-destructive"
          data-testid="switch-to-fos-error"
        >
          {errorMsg}
        </p>
      )}
    </div>
  );
}
